import React, { useCallback, useEffect, useState } from 'react';
import { rosterAPI } from '../../../services/api';
import { Alert, Badge, Button, ConfirmDialog, Select } from '../../../components/ui';
import RosterProfileEditor from './RosterProfileEditor';
import { currentMonth, editRosterWeek, hasWeeklyAssignments, monthLabel, rosterWeeks, sortRosterRows, weeklyCoverage, PROJECT_BACKGROUNDS, projectBackground, SHIFT_CLASSES, SHIFT_LABELS, weekday } from './rosterUtils';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const YEARS = Array.from({ length: 101 }, (_, index) => String(2000 + index));

const loadErrorMessage = (error, resource) => {
  const status = error.response?.status;
  if (status === 404) return 'The connected server does not provide the roster API. The backend must be updated before roster setup and generation can work.';
  if (status === 401 || status === 403) return `You do not have access to ${resource}. Sign in again with an authorized account.`;
  if (!error.response) return `Unable to reach the server to load ${resource}. Check your connection and try Reload roster.`;
  return error.response.data?.message || `Unable to load ${resource} (server response ${status}). Try Reload roster.`;
};

export default function ShiftRosterTab({ role, onDirtyChange, onBusyChange }) {
  const isAdmin = role === 'admin';
  const [month, setMonth] = useState(currentMonth);
  const [roster, setRoster] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [selected, setSelected] = useState('');
  const [dirty, setDirty] = useState(false);
  const [profileDirty, setProfileDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [rosterLoadError, setRosterLoadError] = useState('');
  const [profilesLoadError, setProfilesLoadError] = useState('');
  const [setupOpen, setSetupOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [reload, setReload] = useState(0);
  const busy = loading || working;
  useEffect(() => { onDirtyChange(dirty || profileDirty); }, [dirty, profileDirty, onDirtyChange]);
  useEffect(() => { onBusyChange(busy); }, [busy, onBusyChange]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(''); setRosterLoadError(''); setProfilesLoadError(''); setMessage(''); setRoster(null); setEmployees([]); setDirty(false);
    const requests = [rosterAPI.getMonth(month), isAdmin ? rosterAPI.getProfiles() : Promise.resolve(null)];
    Promise.allSettled(requests).then(([result, profiles]) => {
      if (cancelled) return;
      if (result.status === 'fulfilled') {
        if (result.value.data && Object.prototype.hasOwnProperty.call(result.value.data, 'roster')) {
          setRoster(result.value.data.roster);
        } else setRosterLoadError('The server returned an unexpected roster response. Check that this website is connected to the roster backend.');
      } else setRosterLoadError(loadErrorMessage(result.reason, 'the roster'));
      if (isAdmin) {
        if (profiles.status === 'fulfilled' && Array.isArray(profiles.value.data?.employees)) {
          const entries = profiles.value.data.employees;
          setEmployees(entries);
          if (!entries.some((entry) => entry.profile?.active)) setSetupOpen(true);
        } else {
          setProfilesLoadError(profiles.status === 'rejected'
            ? loadErrorMessage(profiles.reason, 'employee setup')
            : 'The server returned an unexpected employee list. Check the roster backend connection.');
        }
      }
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [month, isAdmin, reload]);

  const run = async (action) => {
    setWorking(true); setError(''); setMessage('');
    try { await action(); } catch (err) { setError(err.response?.data?.message || err.message || 'Unable to update the roster.'); }
    finally { setWorking(false); }
  };
  const discardThen = (action) => {
    if (dirty || profileDirty) setPendingAction(() => action);
    else action();
  };
  const changeMonth = (value) => {
    if (value === month) return;
    discardThen(() => { setMonth(value); setProfileDirty(false); setSelected(''); });
  };
  const generate = () => run(async () => {
    const result = await rosterAPI.generate(month);
    const generated = result.data?.roster;
    if (!hasWeeklyAssignments(generated)) {
      throw new Error('The server did not return the required weekly assignments. Update the connected roster backend to the weekly scheduling version, then reload this page and generate again. Your current roster has not been replaced.');
    }
    setRoster(generated); setDirty(true);
    setMessage('Weekly roster generated using the previous saved month to continue employee rotations, or the employee anchor date when no reference is available. Review individual weeks, then save.');
  });
  const save = () => run(async () => {
    const result = await rosterAPI.save(roster);
    setRoster(result.data.roster); setDirty(false); setMessage('Roster saved. Employees can now view their assigned shifts.');
  });
  const saveProfile = (profile) => run(async () => {
    const result = await rosterAPI.saveProfile(selected, profile);
    setEmployees((old) => old.map((employee) => employee.userId === selected ? { ...employee, profile: result.data.profile } : employee));
    setProfileDirty(false); setMessage('Employee setup saved. Generate the month to apply their schedule; saved weekly edits are retained.');
  });
  const updateWeek = (userId, start, code) => {
    setRoster((old) => editRosterWeek(old, userId, start, code)); setDirty(true); setMessage('');
  };
  const onProfileDirty = useCallback((value) => setProfileDirty(value), []);
  const includedCount = employees.filter((entry) => entry.profile?.active).length;
  const loadFailed = Boolean(rosterLoadError || profilesLoadError);
  const employee = employees.find((entry) => entry.userId === selected);
  const dates = roster?.rows[0]?.days.map((day) => day.date) || [];

  const weeks = rosterWeeks(dates);
  const coverage = weeklyCoverage(roster?.rows || []);
  const legacy = Boolean(roster?.rows.length && !hasWeeklyAssignments(roster));

  return <div className="space-y-5">
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div className="flex flex-wrap gap-3">
        <Select label="Roster month" className="sm:!w-52" value={month.slice(5)} disabled={working}
          onChange={(event) => changeMonth(`${month.slice(0, 4)}-${event.target.value}`)}>
          {MONTHS.map((label, index) => <option key={label} value={String(index + 1).padStart(2, '0')}>{label}</option>)}
        </Select>
        <Select label="Roster year" className="sm:!w-32" value={month.slice(0, 4)} disabled={working}
          onChange={(event) => changeMonth(`${event.target.value}-${month.slice(5)}`)}>
          {YEARS.map((year) => <option key={year} value={year}>{year}</option>)}
        </Select>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button variant="secondary" disabled={busy} onClick={() => discardThen(() => { setReload((old) => old + 1); setSelected(''); setProfileDirty(false); })}>Reload roster</Button>
        {isAdmin && <>
          <Button variant="secondary" disabled={busy || profileDirty || loadFailed || includedCount === 0} onClick={() => discardThen(generate)}>Generate month</Button>
          <Button disabled={busy || !dirty || !roster?.rows.length || profileDirty || legacy} onClick={save}>Save roster</Button>
          <Button variant="ghost" disabled={busy || dirty || !roster?.rows.length || roster.version === null}
            onClick={() => run(async () => { const { exportRoster } = await import('./exportRoster'); await exportRoster(roster); })}>Export Excel</Button>
          <Button variant="ghost" disabled={busy || dirty || !roster?.rows.length || roster.version === null}
            onClick={() => run(async () => { const { exportRosterPdf } = await import('./exportRosterPdf'); exportRosterPdf(roster); })}>Export PDF</Button>
        </>}
      </div>
    </div>
    <div className="flex flex-wrap items-center gap-2">
      <Badge variant={dirty ? 'warning' : 'neutral'}>{loading ? 'Loading roster' : rosterLoadError ? 'Roster unavailable' : dirty ? 'Unsaved preview' : roster ? 'Saved roster' : 'No saved roster'}</Badge>
      {roster?.updatedAt && <span className="text-xs text-muted-fg">Saved {new Date(roster.updatedAt).toLocaleString('en-GB', { timeZone: 'Asia/Qatar' })} (Qatar)</span>}
    </div>
    {rosterLoadError && <Alert variant="error">{rosterLoadError}</Alert>}
    {profilesLoadError && profilesLoadError !== rosterLoadError && <Alert variant="error">{profilesLoadError}</Alert>}
    {isAdmin && !loading && !profilesLoadError && <Alert variant="info">
      <p><strong>{includedCount} employee{includedCount === 1 ? '' : 's'} included.</strong> 1. Save each employee’s schedule and vacations. 2. Generate from those individual patterns. 3. Review or edit whole weeks, then save.</p>
      <Button variant="secondary" className="mt-3" onClick={() => { setSetupOpen(true); document.getElementById('roster-employee-setup')?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' }); }}>Configure employees</Button>
    </Alert>}
    {error && <Alert variant="error">{error}</Alert>}
    {message && <Alert variant="info">{message}</Alert>}
    {isAdmin && roster?.warnings?.length > 0 && <Alert variant="warning">
      <ul className="list-disc space-y-1 pl-5">{roster.warnings.map((warning, index) => <li key={index}>{warning}</li>)}</ul>
    </Alert>}
    {isAdmin && legacy && <Alert variant="warning">This roster uses the old daily scheduling format. Generate the month from each employee’s schedule to enable weekly editing.</Alert>}
    {isAdmin && coverage.length > 0 && <details className="rounded-lg border border-border p-4" open>
      <summary className="cursor-pointer font-semibold">Weekly project coverage</summary>
      <p className="my-2 text-xs text-muted-fg">Staff per working day (minimum–maximum). FM020 and FM030 share one pool; FM014 has no separate target. Informational only: staffing gaps do not change individual schedules.</p>
      <div className="overflow-auto"><table className="w-full text-sm"><thead><tr>{['Week starting', 'Staffing pool', 'A', 'B', 'C', 'Coverage'].map(label => <th key={label} className="p-2 text-left">{label}</th>)}</tr></thead>
        <tbody>{coverage.map(group => {
          const uncovered = group.days.some(day => Math.min(day.A, day.B, day.C) === 0);
          const uneven = group.days.some(day => Math.max(day.A, day.B, day.C) - Math.min(day.A, day.B, day.C) > 1);
          return <tr key={`${group.pool}-${group.start}`} className={uncovered || uneven ? 'bg-warning-soft' : ''}>
            <td className="p-2">{group.start}</td><td className="p-2">{group.pool}</td>
            {['A', 'B', 'C'].map(code => { const values = group.days.map(day => day[code]); const low = Math.min(...values), high = Math.max(...values); return <td key={code} className="p-2">{low === high ? low : `${low}–${high}`}</td>; })}
            <td className="p-2">{uncovered ? 'Uncovered shift' : uneven ? 'Uneven staffing' : 'Balanced'}</td>
          </tr>;
        })}</tbody></table></div>
    </details>}
    {loading ? <p role="status" className="text-sm text-muted-fg">Loading roster…</p> : roster?.rows.length ? <>
      <div className="overflow-x-auto rounded-lg border border-border" tabIndex={0} role="region" aria-label="Monthly shift roster">
        <table className="w-full border-collapse text-sm">
          <caption className="sr-only">{monthLabel(month)} shift roster. Fridays are weekly off.</caption>
          <thead className="sticky top-0 z-20 bg-muted">
            <tr>
              <th scope="col" className="sticky left-0 z-30 min-w-48 border-b border-border bg-muted p-3 text-left">Employee</th>
              {dates.map((date) => <th scope="col" key={date} className={`min-w-14 border-b border-border px-2 py-2 text-center ${weekday(date) === 'Fri' ? 'bg-neutral-soft' : ''}`}>
                <span className="block text-xs font-normal text-muted-fg">{weekday(date)}</span>{Number(date.slice(-2))}
              </th>)}
            </tr>
          </thead>
          <tbody>{sortRosterRows(roster.rows).map((row) => <tr key={row.userId} className={projectBackground(row.project)}>
            <th scope="row" className={`sticky left-0 z-10 border-b border-border px-3 py-2 text-left font-medium before:absolute before:inset-y-2 before:left-0 before:w-[3px] before:rounded-r before:bg-[var(--project-accent,transparent)] ${projectBackground(row.project)}`}>
              {row.name}<span className="block text-xs font-normal text-muted-fg">{[row.designation, row.project].filter(Boolean).join(' · ')}</span>
            </th>
            {weeks.map((group) => {
              const assignment = row.weeks?.find(week => week.start === group.start);
              return <td key={group.start} colSpan={group.dates.length} className="border-b border-l border-border p-1 align-top">
                {isAdmin && assignment && !legacy && <select aria-label={`${row.name} week ${group.start}`}
                  value={assignment.manual ? assignment.shift : 'AUTO'} disabled={busy}
                  className="mb-1 min-h-11 w-full rounded border border-border bg-surface px-2 text-sm font-semibold"
                  onChange={(event) => updateWeek(row.userId, group.start, event.target.value)}>
                  <option value="AUTO">{assignment.generatedShift} · employee schedule</option>
                  {['A', 'B', 'C'].map(code => <option key={code} value={code}>{code} · whole week</option>)}
                </select>}
                <div className="grid" style={{ gridTemplateColumns: `repeat(${group.dates.length}, minmax(0, 1fr))` }}>
                  {group.dates.map(date => { const day = row.days.find(entry => entry.date === date); return <span key={date}
                    className={`flex min-h-9 items-center justify-center text-sm font-semibold ${SHIFT_CLASSES[day.shift]}`} title={`${date}: ${SHIFT_LABELS[day.shift]}`}>{day.shift}</span>; })}
                </div>
                {assignment?.manual && <span className="block text-center text-[10px]">Week edited</span>}
              </td>;
            })}
          </tr>)}</tbody>
        </table>
      </div>
      <div aria-label="Project colors" className="flex flex-wrap gap-2 text-xs">
        {Object.entries(PROJECT_BACKGROUNDS).map(([project, background]) => <span key={project} className={`inline-flex items-center gap-2 rounded border border-border px-3 py-1 ${background}`}>
          <span aria-hidden="true" className="h-2 w-2 rounded-full bg-[var(--project-accent)]" />{project}
        </span>)}
      </div>
      <p className="text-xs text-muted-fg">{isAdmin ? 'Edit a whole Saturday–Thursday week. Fridays and vacations stay protected. Saving a week spanning two months also updates its saved neighboring month. Save before exporting.' : 'Your saved assignments are shown above. Contact your administrator for changes.'}</p>
    </> : !loading && !rosterLoadError && <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-fg">
      {isAdmin ? 'Configure employee scheduling below, then generate and save this month.' : 'No saved assignments for you this month.'}
    </div>}
    <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-muted-fg">
      {Object.entries(SHIFT_LABELS).map(([code, label]) => <span key={code}><strong>{code}</strong> {label}</span>)}
    </div>
    <p className="text-xs text-muted-fg">All shift times are in Qatar time. Weeks start Saturday; every Friday is off. This roster does not change attendance or overtime calculations.</p>
    {isAdmin && <details id="roster-employee-setup" open={setupOpen} onToggle={(event) => setSetupOpen(event.currentTarget.open)} className="rounded-lg border border-border bg-muted/20 p-4">
      <summary className="cursor-pointer text-sm font-semibold">Employee scheduling & vacations {profileDirty ? '· Unsaved setup' : ''}</summary>
      <div className="mt-4 space-y-4">
        {!loading && !profilesLoadError && employees.length === 0 && <Alert variant="info">No employee accounts are available. Create employee accounts in Settings → User management, then reload the roster.</Alert>}
        <Select label="Employee account" value={selected} disabled={busy || Boolean(profilesLoadError)} onChange={(event) => {
          const value = event.target.value;
          if (profileDirty) setPendingAction(() => () => { setSelected(value); setProfileDirty(false); });
          else setSelected(value);
        }}><option value="">Select an employee</option>{employees.map((entry) => <option key={entry.userId} value={entry.userId}>
          {entry.profile?.name || entry.username} ({entry.username}){entry.profile?.active ? ' — included' : ' — not included'}
        </option>)}</Select>
        {employee && <RosterProfileEditor key={`${selected}-${employee.profile?.updatedAt || 'new'}`} employee={employee} month={month}
          onSave={saveProfile} onDirtyChange={onProfileDirty} busy={busy} />}
      </div>
    </details>}
    <ConfirmDialog isOpen={Boolean(pendingAction)} onClose={() => setPendingAction(null)} title="Discard unsaved changes?"
      message="Continue using the last saved data? Unsaved changes for this action will be discarded." confirmLabel="Discard and continue"
      onConfirm={() => { const action = pendingAction; setPendingAction(null); action(); }} />
  </div>;
}
